/**
 * Zentraler Loader fuer /portal/tenant-settings mit Module-Level-Cache.
 * Eine Fetch pro Session — Settings aendern sich selten und werden ueber
 * TenantSettingsAdminPage beim Speichern explizit per setTenantSettings()
 * im Cache aktualisiert.
 */
import { useEffect, useState } from 'react';
import { api } from '@onvice/portal-shell-api';
import type { RegionCode } from '@onvice/portal-ui';

export type TenantSettings = {
  region: RegionCode;
  assistantMascotUrl: string | null;
  // 2026-08-01 (Joerg): Test-Cockpit im Portal tenant-weit sichtbar?
  // Blendet Sidebar-/Header-Link, Drawer + PersonaSwitcher aus wenn false.
  // Default true — backwards-compat fuer alle Tenants ohne gesetzte Setting.
  testCockpitEnabled: boolean;
};

const DEFAULT: TenantSettings = { region: 'NW', assistantMascotUrl: null, testCockpitEnabled: true };

let cached: TenantSettings | null = null;
let inflight: Promise<TenantSettings> | null = null;
const listeners = new Set<(s: TenantSettings) => void>();

type ApiResponse = {
  success: boolean;
  data: { region?: RegionCode; assistant_mascot_url?: string | null; test_cockpit_enabled?: boolean };
};

function normalize(raw: ApiResponse['data']): TenantSettings {
  return {
    region: (raw.region as RegionCode) ?? 'NW',
    assistantMascotUrl: raw.assistant_mascot_url ? String(raw.assistant_mascot_url) : null,
    testCockpitEnabled: raw.test_cockpit_enabled !== false,
  };
}

async function load(): Promise<TenantSettings> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await api.get<ApiResponse>('/portal/tenant-settings');
      cached = r.success ? normalize(r.data) : DEFAULT;
    } catch {
      cached = DEFAULT;
    } finally {
      inflight = null;
    }
    return cached!;
  })();
  return inflight;
}

/** Wird vom Admin-PUT aufgerufen, damit alle Subscriber die neuen Werte sehen. */
export function setTenantSettings(next: Partial<TenantSettings>): void {
  cached = { ...(cached ?? DEFAULT), ...next };
  listeners.forEach((fn) => fn(cached!));
}

export function useTenantSettings(): TenantSettings {
  const [state, setState] = useState<TenantSettings>(() => cached ?? DEFAULT);
  useEffect(() => {
    let cancelled = false;
    load().then((s) => { if (!cancelled) setState(s); });
    const fn = (s: TenantSettings) => setState(s);
    listeners.add(fn);
    return () => { cancelled = true; listeners.delete(fn); };
  }, []);
  return state;
}
